import React, { useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Star, GraduationCap, Truck, Palette, Sparkles, ExternalLink, CalendarCheck, Calculator, Phone } from "lucide-react";
import { Reveal } from "./components";
import { MEDIA, SERVICES, PORTFOLIO, AREAS, TESTIMONIALS, FAQ, STATS, SEEN_LOGOS, PAINTS, BLOG } from "./data";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CountUp({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const match = /^(\d+(?:\.\d+)?)(\+)?$/.exec(value);
  const decimals = match && match[1].includes(".") ? 1 : 0;
  const [display, setDisplay] = useState(match ? (decimals ? "0.0" : "0") + (match[2] || "") : value);

  useEffect(() => {
    if (!match || !inView) return;
    const target = parseFloat(match[1]);
    const suffix = match[2] || "";
    const duration = 1600;
    let startTime;
    let raf;
    const tick = (t) => {
      if (startTime === undefined) startTime = t;
      const p = Math.min((t - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay((target * eased).toFixed(decimals) + suffix);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target.toFixed(decimals) + suffix);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  return <span ref={ref}>{display}</span>;
}

function SectionHead({ over, title, light, center }) {
  return (
    <div className={`mb-14 ${center ? "text-center flex flex-col items-center" : ""}`}>
      <h2 className={`font-serif text-4xl md:text-6xl ${light ? "text-white" : "text-ink"}`}>{title}</h2>
      <p className={`eyebrow text-gold mt-4 ${center ? "eyebrow-center" : ""}`}>{over}</p>
    </div>
  );
}

export function Home() {
  return (
    <div data-testid="home-page">
      {/* ============ 1 · HERO ============ */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img src={MEDIA.heroImage} alt="Luxury Beverly Hills modern estate at dusk" className="hero-video animate-kenburns" />
        <div className="absolute inset-0 bg-black/50" />
        {/* Phone banner — top of hero, below the fixed nav */}
        <a
          href="tel:+13108456163"
          data-testid="hero-phone-banner"
          aria-label="Call Beverly Hills Paint Co. at 310-845-6163"
          className="absolute top-16 md:top-20 left-0 right-0 z-20 bg-gold/95 backdrop-blur-sm text-white hover:bg-gold-dark transition-colors"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 md:py-4 flex items-center justify-center gap-3">
            <Phone size={18} className="shrink-0" />
            <span className="font-serif text-xl md:text-2xl tracking-wide whitespace-nowrap">310-845-6163</span>
            <span className="hidden sm:inline text-white/85 text-xs uppercase tracking-[0.25em] ml-2">Call for a free estimate</span>
          </div>
        </a>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl mt-32 md:mt-36">
            <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">Flawless finishes for the finest homes in Los Angeles</h1>
            <p className="text-white/80 text-lg mt-6 max-w-xl leading-relaxed">Luxury residential &amp; commercial painting with 10+ years of experience and an in-house color designer to make choosing your perfect palette effortless.</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl" data-testid="hero-cta-grid">
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="bg-gold text-white overline px-6 py-4 hover:bg-gold-dark transition-colors flex items-center justify-center gap-2 text-center"
              >
                Book a Free Estimate <ArrowRight size={14} />
              </Link>
              <Link
                to="/consult"
                data-testid="hero-consult-btn"
                className="border border-white/70 text-white overline px-6 py-4 hover:bg-white hover:text-ink transition-colors text-center"
              >
                Book a 1:1 Color Consultation
              </Link>
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="border border-white/70 text-white overline px-6 py-4 hover:bg-white hover:text-ink transition-colors text-center"
              >
                Shop Courses &amp; Renderings
              </Link>
            </div>
            <div className="mt-12" data-testid="hero-menu">
              <p className="overline text-white/60 mb-4 flex items-center gap-2">Explore <ArrowRight size={13} /></p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-2xl">
                {[
                  ["Services", "/services"],
                  ["Portfolio", "/portfolio"],
                  ["Service Areas", "/service-areas"],
                  ["Earn with Referrals", "/refer"],
                  ["Our Blog", "/blog"],
                  ["About", "/about"],
                ].map(([label, to]) => (
                  <Link
                    key={label}
                    to={to}
                    data-testid={`hero-link-${label.toLowerCase().replace(/ /g,"-")}`}
                    className="border border-white/40 text-white overline px-4 py-2.5 backdrop-blur-sm bg-white/5 hover:bg-gold hover:border-gold transition-colors text-center"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* As Seen On + Stats — thin trust ribbons */}
      <section data-testid="as-seen-on" className="bg-white border-y border-black/10 py-12">
        <p className="text-center eyebrow eyebrow-center text-ink/50 mb-9 mx-auto w-fit">As Seen On</p>
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap items-center justify-center gap-x-16 gap-y-8">
          {SEEN_LOGOS.map((logo) => (
            <img
              key={logo.name}
              src={logo.url}
              alt={`${logo.name} logo`}
              data-testid={`seen-logo-${logo.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`${logo.h} w-auto object-contain mix-blend-multiply`}
            />
          ))}
        </div>
      </section>

      {/* ============ Latest from Instagram (removed per user request) ============ */}
      <section className="bg-ivory py-16 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center">
              <p className="font-serif text-4xl md:text-5xl text-ink"><CountUp value={s.value} /></p>
              <p className="eyebrow eyebrow-center text-gold mt-3 mx-auto w-fit">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUR CRAFT — photo + intro (restored) */}
      <section data-testid="home-craft" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-2 gap-16 items-center">
        <Reveal>
          <p className="overline text-gold">Our Craft</p>
          <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Every project completed with care, precision<br />&amp; a commitment to exceeding expectations</h2>
          <p className="text-muted-foreground mt-6 leading-relaxed">We specialize in transforming estates and commercial spaces across Los Angeles. From meticulous surface preparation to the final coat, our team treats every home like a masterpiece.</p>
          <ul className="mt-8 space-y-3">
            {["Meticulous preparation","Premium low-VOC paints","White-glove, respectful service"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm"><Check size={16} className="text-gold" /> {t}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <img src={MEDIA.painterWorking} alt="Painter at work" className="w-full h-[520px] object-cover" />
        </Reveal>
      </section>

      {/* ============ 2 · SERVICES ============ */}
      <section data-testid="home-services" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead over="What We Do" title="Services" />
          <div className="grid md:grid-cols-3 gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div data-testid={`service-card-${i}`} className="group border border-black/10 bg-ivory hover:shadow-[0_24px_50px_-20px_rgba(0,0,0,0.15)] transition-shadow duration-500">
                  <div className="overflow-hidden"><img src={s.img} alt={s.title} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="p-8">
                    <h3 className="font-serif text-2xl">{s.title}</h3>
                    <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 3 · BOOK A FREE ESTIMATE (small band) ============ */}
      <section data-testid="home-estimate-band" className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="eyebrow text-gold flex items-center gap-2"><CalendarCheck size={15} /> Complimentary</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Book a free, no-obligation estimate.</h2>
            <p className="text-white/70 mt-3 leading-relaxed">A designer visits your home, walks the project with you and returns a detailed proposal within 24 hours.</p>
          </div>
          <Link to="/contact" data-testid="home-estimate-btn" className="shrink-0 inline-flex items-center justify-center gap-2 bg-gold text-white overline px-9 py-4 hover:bg-gold-dark transition-colors">
            Book a Free Estimate <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* ============ 4 · IN-HOUSE COLOR DESIGNER ============ */}
      <section data-testid="home-designer" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-2 gap-16 items-center">
        <Reveal>
          <img src={MEDIA.interiorLuxury} alt="In-house color designer" className="w-full h-[520px] object-cover" />
        </Reveal>
        <Reveal delay={0.12}>
          <p className="eyebrow text-gold">Color Selection, Simplified</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-3">A dedicated in-house designer for your project</h2>
          <p className="text-muted-foreground mt-6 leading-relaxed">With 10+ years of experience, we specialize in color selection. Our in-house designer works one-on-one with you to choose the perfect palette for your home — making the entire experience effortless and stress-free.</p>
          <ul className="mt-8 space-y-3">
            {["Personalized palette consultation","10+ years of luxury painting experience","Guidance from concept to final coat"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm"><Palette size={16} className="text-gold" /> {t}</li>
            ))}
          </ul>
          <Link to="/consult" data-testid="designer-cta" className="inline-flex items-center gap-2 overline text-ink mt-8 hover:text-gold">Book a 1:1 Color Consult <ArrowRight size={15} /></Link>
        </Reveal>
      </section>

      {/* ============ 5 · HOLLYWOOD BANNER → SERVICE AREAS → PORTFOLIO ============ */}
      <section data-testid="home-hollywood" className="relative py-32 overflow-hidden">
        <video className="hero-video" autoPlay loop muted playsInline poster={MEDIA.mansion}>
          <source src={MEDIA.overviewVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-white text-4xl md:text-6xl leading-tight">Painting the homes of Hollywood, Beverly Hills, Bel Air, Malibu and all of Los Angeles</h2>
            <p className="eyebrow eyebrow-center text-gold mt-5 mx-auto w-fit">The Los Angeles Lifestyle</p>
            <p className="text-white/80 mt-6 max-w-2xl mx-auto">From hillside estates to oceanfront villas, we bring a level of finish worthy of the city&apos;s most iconic addresses.</p>
          </Reveal>
        </div>
      </section>

      {/* SERVICE AREAS (moved above Portfolio) */}
      <section data-testid="home-service-areas" className="bg-white py-24 md:py-32 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead over="Where We Work" title="Service Areas" />
          <p className="text-muted-foreground max-w-2xl -mt-10 mb-14">We serve luxury estates and commercial properties across the finest addresses in Los Angeles County.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-8 gap-y-4">
            {AREAS.map((a, i) => (
              <Reveal key={a} delay={i * 0.03}>
                <div className="flex items-center gap-3 border-b border-black/10 py-3">
                  <span className="w-1 h-1 rounded-full bg-gold" />
                  <span className="font-serif text-lg text-ink">{a}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/service-areas" data-testid="home-areas-cta" className="inline-flex items-center gap-2 overline text-ink hover:text-gold">See all service areas <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      <section data-testid="home-portfolio" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <SectionHead over="Selected Work" title="Portfolio" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {PORTFOLIO.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08} className={p.span}>
              <Link to="/portfolio" className="relative block overflow-hidden group h-80">
                <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-6 left-6"><p className="overline text-gold">{p.loc}</p><p className="font-serif text-white text-2xl">{p.name}</p></div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <Link to="/portfolio" data-testid="home-portfolio-cta" className="inline-flex items-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors">View the full portfolio <ArrowRight size={15} /></Link>
        </div>
      </section>

      {/* ============ 6 · SHOP OUR COURSES / RENDERINGS / CONSULT ============ */}
      <section data-testid="shop-highlight" className="bg-white py-24 md:py-32 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold">Learn · Visualize · Consult</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight mt-3">Shop our color courses, renderings and 1:1 consults.</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl">Learn our color-selection framework, see your space rendered in your final palette before a single brushstroke, and book a private session with our in-house designer.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
            {[
              { label: "Color Selection Courses", Icon: GraduationCap, img: MEDIA.swatches, title: "Choose color like a pro", desc: "Designer-led video courses teaching the exact framework we use for luxury estates.", cta: "Shop Courses", to: "/courses", testid: "highlight-courses-btn" },
              { label: "Renderings", Icon: Sparkles, img: MEDIA.renderScreen, title: "See it before you paint", desc: "Photorealistic renderings of your space in your chosen colors, delivered digitally.", cta: "Shop Renderings", to: "/renderings", testid: "highlight-renderings-btn" },
              { label: "1:1 Color Consult", Icon: Palette, img: MEDIA.rendering, title: "Book a private consult", desc: "Work one-on-one with our designer to lock in the perfect palette for your home.", cta: "Book Consult", to: "/consult", testid: "highlight-consult-btn" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <div className="group flex flex-col h-full">
                  <p className="font-sans font-bold text-ink text-sm uppercase tracking-wider mb-4 flex items-center gap-2"><c.Icon size={16} className="text-gold" /> {c.label}</p>
                  <div className="overflow-hidden h-60"><img src={c.img} alt={c.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <h3 className="font-serif text-2xl mt-5">{c.title}</h3>
                  <p className="text-muted-foreground text-sm mt-2 leading-relaxed flex-1">{c.desc}</p>
                  <Link to={c.to} data-testid={c.testid} className="inline-flex items-center gap-2 overline text-ink mt-5 hover:text-gold transition-colors">{c.cta} <ArrowRight size={15} /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 7 · COLOR QUIZ CTA ============ */}
      <section data-testid="home-quiz-cta" className="bg-ivory border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="flex gap-2 mb-6">
              {["#EDE7DC", "#C9C1B6", "#B4CCC9", "#9DB4A0", "#B07A57", "#8FA0B0"].map((c) => (
                <span key={c} className="h-14 flex-1 rounded" style={{ background: c }} />
              ))}
            </div>
            <p className="overline text-gold">Free Interactive Tool</p>
            <h2 className="font-serif text-3xl md:text-5xl mt-3 leading-tight">Not sure where to start with color?</h2>
            <p className="text-muted-foreground mt-5 leading-relaxed max-w-xl">Take our interactive Color Quiz and get three real, verified Sherwin-Williams and Benjamin Moore colors chosen for your room, your light and your style. It takes about a minute, and there&apos;s no account required.</p>
            <Link to="/color-quiz" data-testid="home-quiz-btn" className="inline-flex items-center gap-2 bg-ink text-white overline px-9 py-4 mt-8 hover:bg-gold transition-colors">Take the Color Quiz <ArrowRight size={15} /></Link>
            <Link to="/sheen-quiz" data-testid="home-sheen-btn" className="inline-flex items-center gap-2 overline text-ink px-4 py-4 mt-8 sm:ml-3 hover:text-gold transition-colors">Or find your paint finish <ArrowRight size={15} /></Link>
          </Reveal>
          <Reveal delay={0.12}>
            <img src={MEDIA.swatches} alt="Curated paint color swatches" className="w-full h-[420px] object-cover" />
          </Reveal>
        </div>
      </section>

      {/* ============ 8 · TESTIMONIALS ============ */}
      <section data-testid="home-testimonials" className="bg-ivory py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead over="Client Love" title="Testimonials" />
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <div data-testid={`testimonial-${i}`} className="border border-black/10 bg-white p-8 h-full">
                  <div className="flex gap-1 text-gold mb-4">{[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}</div>
                  <p className="text-ink leading-relaxed font-serif text-xl italic">&ldquo;{t.text}&rdquo;</p>
                  <p className="overline text-gold mt-6">{t.name} · {t.loc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Logo band + Socials + Final Quote CTA */}
      <section data-testid="logo-band" className="bg-white pt-16 md:pt-20 pb-2 border-t border-black/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <img src={MEDIA.logo} alt="Beverly Hills Paint Co. logo" className="h-36 md:h-44 w-auto mx-auto" />
        </div>
      </section>

      <SocialConnect />
      <QuoteCTA />
    </div>
  );
}

export function SocialConnect() {
  return (
    <section data-testid="social-connect" className="bg-white py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="font-serif text-4xl md:text-6xl">Connect with us</h2>
        <p className="eyebrow eyebrow-center text-gold mt-4 mx-auto w-fit">Follow The Journey</p>
        <p className="text-muted-foreground mt-5">Behind-the-scenes transformations, color inspiration, and the finest homes in LA.</p>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <a href="https://www.instagram.com/beverlyhillspaint" target="_blank" rel="noreferrer" data-testid="connect-instagram" className="border border-ink px-8 py-4 overline hover:bg-ink hover:text-white transition-colors">Instagram</a>
          <a href="https://www.tiktok.com/@beverlyhillspaint" target="_blank" rel="noreferrer" data-testid="connect-tiktok" className="border border-ink px-8 py-4 overline hover:bg-ink hover:text-white transition-colors">TikTok</a>
          <span className="border border-black/20 text-muted-foreground px-8 py-4 overline">YouTube · Coming Soon</span>
        </div>
      </div>
    </section>
  );
}

export function QuoteCTA() {
  return (
    <section className="bg-ink py-20">
      <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
        <h2 className="font-serif text-4xl md:text-5xl text-white">Ready to transform your home?</h2>
        <Link to="/contact" data-testid="cta-quote-btn" className="bg-gold text-white overline px-10 py-5 hover:bg-gold-dark transition-colors whitespace-nowrap">Request Your Free Estimate</Link>
      </div>
    </section>
  );
}

export function Services() {
  return (
    <div data-testid="services-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-serif text-5xl md:text-6xl">Services</h1>
        <p className="eyebrow text-gold mt-4">Our Expertise</p>
        <div className="mt-16 space-y-24">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title}>
              <div className={`grid md:grid-cols-2 gap-12 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                <img src={s.img} alt={s.title} className="w-full h-[420px] object-cover [direction:ltr]" />
                <div className="[direction:ltr]">
                  <h2 className="font-serif text-4xl">{s.title}</h2>
                  <p className="eyebrow text-gold mt-3">0{i + 1}</p>
                  <p className="text-muted-foreground mt-4 leading-relaxed">{s.desc} Our craftsmen handle every detail, from protecting your furnishings to the final inspection walkthrough.</p>
                  <Link to="/contact" className="inline-flex items-center gap-2 overline text-ink mt-6 hover:text-gold">Get a quote <ArrowRight size={15} /></Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <QuoteCTA />
    </div>
  );
}

export function Portfolio() {
  return (
    <div data-testid="portfolio-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-serif text-5xl md:text-6xl">Portfolio</h1>
        <p className="eyebrow text-gold mt-4">Selected Work</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-14">
          {[...PORTFOLIO, ...PORTFOLIO].map((p, i) => (
            <Reveal key={i} delay={(i % 4) * 0.08} className={p.span}>
              <div className="relative block overflow-hidden group h-80">
                <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-6 left-6"><p className="overline text-gold">{p.loc}</p><p className="font-serif text-white text-2xl">{p.name}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <QuoteCTA />
    </div>
  );
}

export function About() {
  return (
    <div data-testid="about-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block bg-white p-6 border border-black/10 mb-8">
              <img src={MEDIA.logo} alt="Beverly Hills Paint Co. logo" className="h-28 w-auto" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight">Craftsmanship worthy of the 90210</h1>
            <p className="eyebrow text-gold mt-4">Our Story</p>
            <p className="text-muted-foreground mt-6 leading-relaxed">Beverly Hills Paint Co. was founded on a simple belief: the finest homes deserve the finest finishes. With <span className="text-ink font-medium">over 10 years of experience</span>, we specialize in high-quality residential and commercial painting, and in color selection.</p>
            <p className="text-muted-foreground mt-4 leading-relaxed">Our <span className="text-ink font-medium">dedicated in-house designer</span> works with you one-on-one to choose the perfect color, making the experience amazing and simple. Every project is completed with care, precision, and a commitment to exceeding expectations, from the first consultation to the final walkthrough.</p>
          </div>
          <img src={MEDIA.interiorLuxury} alt="Luxury interior" className="w-full h-[560px] object-cover" />
        </div>
      </div>
      <SocialConnect />
    </div>
  );
}

export function ServiceAreas() {
  return (
    <div data-testid="service-areas-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-serif text-5xl md:text-6xl">Our Service Areas</h1>
        <p className="eyebrow text-gold mt-4">Where We Work</p>
        <p className="text-muted-foreground mt-5 max-w-2xl">We proudly serve residential and commercial clients across the most prestigious neighborhoods of Los Angeles.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 mt-14 border border-black/10">
          {AREAS.map((a) => (
            <div key={a} data-testid={`area-${a.toLowerCase().replace(/ /g, "-")}`} className="bg-ivory p-6 font-serif text-xl hover:bg-white transition-colors">{a}</div>
          ))}
        </div>
        <div className="mt-24">
          <h2 className="font-serif text-4xl">Frequently Asked Questions</h2>
          <p className="eyebrow text-gold mt-4 mb-8">Still Not Sure?</p>
          <Accordion type="single" collapsible className="max-w-3xl">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="font-serif text-xl text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
      <QuoteCTA />
    </div>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", service_area: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", project_type: "", service_area: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-2 gap-16">
        <div>
          <h1 className="font-serif text-5xl md:text-6xl">Request a Quote</h1>
          <p className="eyebrow text-gold mt-4">Get Started</p>
          <p className="text-muted-foreground mt-6 leading-relaxed">Tell us about your project and our team will reach out to schedule a complimentary on-site consultation.</p>
          <div className="mt-10 space-y-2 text-sm">
            <p className="eyebrow text-gold mb-3">Contact</p>
            <p>(310) 845-6163</p>
            <p>hello@beverlyhillspaintco.com</p>
            <p>Beverly Hills, CA 90210</p>
          </div>
        </div>
        <form onSubmit={submit} data-testid="quote-form" className="bg-white border border-black/10 p-8 space-y-4">
          {[["name","Full name"],["email","Email"],["phone","Phone"]].map(([k, ph]) => (
            <input key={k} placeholder={ph} value={form[k]} onChange={set(k)} data-testid={`field-${k}`} className="w-full border border-black/20 px-4 py-3 bg-ivory text-sm" />
          ))}
          <select value={form.project_type} onChange={set("project_type")} data-testid="field-project" className="w-full border border-black/20 px-4 py-3 bg-ivory text-sm">
            <option value="">Project type</option><option>Residential</option><option>Commercial</option><option>Specialty Finish</option>
          </select>
          <select value={form.service_area} onChange={set("service_area")} data-testid="field-area" className="w-full border border-black/20 px-4 py-3 bg-ivory text-sm">
            <option value="">Service area</option>{AREAS.map((a) => <option key={a}>{a}</option>)}
          </select>
          <textarea placeholder="Tell us about your project" value={form.message} onChange={set("message")} data-testid="field-message" rows={4} className="w-full border border-black/20 px-4 py-3 bg-ivory text-sm" />
          <Button type="submit" disabled={loading} data-testid="submit-quote" className="w-full bg-ink text-white rounded-none hover:bg-gold py-6 overline">{loading ? "Sending..." : "Submit Request"}</Button>
        </form>
      </div>
    </div>
  );
}


export function Consult() {
  const included = [
    "60-minute one-on-one session with our designer",
    "Personalized whole-home or single-room palette",
    "Undertone, lighting & sheen guidance",
    "Curated Sherwin-Williams & Benjamin Moore selections",
    "A written summary you can hand to any painter",
  ];
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_area: "", preferred_time: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending"); setErrMsg("");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        project_type: "1:1 Color Consult",
        source: "consult",
      });
      setStatus("ok");
      toast.success("Consult request received. We'll be in touch within 24 hours.");
    } catch (err) {
      setStatus("error");
      setErrMsg("Could not submit — please try again or email us directly.");
    }
  };

  return (
    <div data-testid="consult-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-2 gap-16 items-center">
        <Reveal>
          <img src={MEDIA.consult} alt="1:1 color consultation" className="w-full h-[540px] object-cover" />
        </Reveal>
        <Reveal delay={0.12}>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight">1:1 Design Consult</h1>
          <p className="eyebrow text-gold mt-4">Personal Color Guidance</p>
          <p className="text-muted-foreground mt-6 leading-relaxed">Choosing color is the hardest part, so we make it effortless. Book a private session with our in-house designer and walk away with a palette you love, tailored to your home, your light, and your lifestyle.</p>
          <ul className="mt-8 space-y-3">
            {included.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm"><Check size={16} className="text-gold mt-0.5 shrink-0" /> {t}</li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-4 mt-10">
            <a href="#book" data-testid="consult-book-btn" className="bg-ink text-white overline px-9 py-4 hover:bg-gold transition-colors flex items-center gap-2"><CalendarCheck size={16} /> Book Your Consult</a>
            <Link to="/renderings" className="border border-ink text-ink overline px-9 py-4 hover:bg-ink hover:text-white transition-colors">Add a Rendering</Link>
          </div>
        </Reveal>
      </div>

      {/* Dedicated booking form (does NOT route to /contact) */}
      <section id="book" data-testid="consult-booking-section" className="bg-ivory border-t border-black/10 py-24 md:py-28 scroll-mt-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="eyebrow text-gold">Reserve Your Session</p>
            <h2 className="font-serif text-3xl md:text-5xl mt-3 leading-tight">Book your 1:1 Color Consult</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl">Share a few details and our team will confirm your session — usually within one business day. Consults can be conducted in-home (LA county) or virtually.</p>
          </Reveal>
          {status === "ok" ? (
            <div data-testid="consult-success" className="mt-10 bg-white border border-black/10 p-10 text-center">
              <p className="eyebrow text-gold">Thank You</p>
              <p className="font-serif text-3xl mt-3">Your consult request is in.</p>
              <p className="text-ink/70 mt-3 max-w-xl mx-auto">Our design team will confirm your session within 24 hours. Keep an eye on your inbox — including spam — for scheduling details.</p>
              <button
                type="button"
                onClick={() => { setStatus("idle"); setForm({ name: "", email: "", phone: "", service_area: "", preferred_time: "", message: "" }); }}
                data-testid="consult-book-another"
                className="mt-8 overline text-ink underline underline-offset-4 hover:text-gold"
              >Book another consult</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="consult-form" className="mt-10 grid md:grid-cols-2 gap-5 bg-white border border-black/10 p-8 md:p-10">
              <label className="block">
                <span className="overline text-[10px] text-ink/60">Full name *</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="consult-name" className="mt-1 w-full border border-black/15 focus:border-gold px-3 py-3 text-sm bg-white outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-ink/60">Email *</span>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="consult-email" className="mt-1 w-full border border-black/15 focus:border-gold px-3 py-3 text-sm bg-white outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-ink/60">Phone</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="consult-phone" className="mt-1 w-full border border-black/15 focus:border-gold px-3 py-3 text-sm bg-white outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-ink/60">Neighborhood / service area</span>
                <input value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} data-testid="consult-area" placeholder="e.g. Beverly Hills, Malibu, Virtual" className="mt-1 w-full border border-black/15 focus:border-gold px-3 py-3 text-sm bg-white outline-none" />
              </label>
              <label className="block md:col-span-2">
                <span className="overline text-[10px] text-ink/60">Preferred date & time</span>
                <input value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="consult-time" placeholder="e.g. Weekday mornings, or Feb 20 at 3pm" className="mt-1 w-full border border-black/15 focus:border-gold px-3 py-3 text-sm bg-white outline-none" />
              </label>
              <label className="block md:col-span-2">
                <span className="overline text-[10px] text-ink/60">Tell us about your project</span>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="consult-message" placeholder="Rooms, current colors, style you're after, timeline…" className="mt-1 w-full border border-black/15 focus:border-gold px-3 py-3 text-sm bg-white outline-none" />
              </label>
              {status === "error" && <p className="md:col-span-2 text-sm text-red-600" data-testid="consult-error">{errMsg}</p>}
              <div className="md:col-span-2 flex flex-wrap items-center gap-4 mt-2">
                <button type="submit" disabled={status === "sending"} data-testid="consult-submit" className="bg-gold text-white overline px-10 py-4 hover:bg-gold-dark transition-colors disabled:opacity-60">
                  {status === "sending" ? "Sending…" : "Book my consult"}
                </button>
                <p className="text-xs text-ink/50">No payment required — we confirm your session by email.</p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

export function Paints() {
  return (
    <div data-testid="paints-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-serif text-5xl md:text-6xl">The Paints We Use</h1>
        <p className="eyebrow text-gold mt-4">Only the Finest Coatings</p>
        <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed">We specify premium, professional-grade coatings for every project, selected for durability, depth of color, and a flawless, long-lasting finish worthy of the finest homes.</p>

        <div className="mt-16 space-y-16">
          {PAINTS.map((b, i) => (
            <Reveal key={b.brand} delay={i * 0.08}>
              <div data-testid={`paint-brand-${i}`} className="border border-black/10 bg-white p-8 md:p-12">
                <h2 className="font-serif text-3xl md:text-4xl">{b.brand}</h2>
                <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">{b.blurb}</p>
                <div className="grid md:grid-cols-3 gap-8 mt-10">
                  {b.groups.map((g) => (
                    <div key={g.title} className="border-t border-black/10 pt-5">
                      <p className="eyebrow text-gold mb-4">{g.title}</p>
                      <ul className="space-y-2">
                        {g.items.map((it) => (
                          <li key={it} className="flex items-start gap-2 text-sm"><Check size={15} className="text-gold mt-0.5 shrink-0" /> {it}</li>
                        ))}
                      </ul>
                      {g.links ? (
                        <div className="flex flex-wrap gap-4 mt-5">
                          {g.links.map((l) => (
                            <a key={l.label} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 overline text-ink hover:text-gold transition-colors text-xs">View {l.label} <ExternalLink size={12} /></a>
                          ))}
                        </div>
                      ) : (
                        <a href={g.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 overline text-ink mt-5 hover:text-gold transition-colors text-xs">View products <ExternalLink size={12} /></a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div data-testid="why-both" className="mt-16 bg-ink text-white p-10 md:p-16">
            <h2 className="font-serif text-3xl md:text-4xl">Why we work with both brands</h2>
            <p className="eyebrow text-gold mt-4">It Comes Down to You</p>
            <p className="text-white/80 mt-6 max-w-3xl leading-relaxed">Both Sherwin-Williams and Benjamin Moore are exceptional, professional-grade manufacturers, and we&apos;re proud to work with either. The deciding factor is simple: <span className="text-white font-medium">client preference</span>. Some clients have a brand they know and trust, a color they&apos;ve already fallen in love with, or a specific product their designer specified. We meet you where you are and deliver a flawless finish with the paint you prefer, with no compromises and no upsell.</p>
          </div>
        </Reveal>
      </div>
      <QuoteCTA />
    </div>
  );
}


export function Blog() {
  return (
    <div data-testid="blog-page" className="pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-serif text-5xl md:text-6xl">Our Blog</h1>
        <p className="eyebrow text-gold mt-4">Color, Craft &amp; Inspiration</p>
        <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed">Notes from our designers on choosing color, finishes, and creating flawless interiors and exteriors across Los Angeles.</p>
        <div className="grid md:grid-cols-3 gap-8 mt-14">
          {BLOG.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block border border-black/10 bg-white h-full hover:shadow-[0_24px_50px_-20px_rgba(0,0,0,0.15)] transition-shadow duration-500">
                <div className="overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-7">
                  <p className="eyebrow text-gold">{p.category} · {p.date}</p>
                  <h3 className="font-serif text-2xl mt-3 leading-snug">{p.title}</h3>
                  <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-2 overline text-ink mt-5 group-hover:text-gold transition-colors">Read Article <ArrowRight size={15} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
      <QuoteCTA />
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = BLOG.find((p) => p.slug === slug);
  if (!post) {
    return (
      <div data-testid="blog-post-page" className="pt-32 min-h-screen max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-4xl">Article not found</h1>
        <Link to="/blog" className="inline-block mt-8 border border-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors">Back to Our Blog</Link>
      </div>
    );
  }
  return (
    <div data-testid="blog-post-page" className="pt-32">
      <article className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/blog" className="overline text-gold hover:text-ink transition-colors block">&larr; Our Blog</Link>
        <p className="eyebrow text-gold mt-8">{post.category} · {post.date}</p>
        <h1 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">{post.title}</h1>
        {post.gallery ? (
          <div className="grid sm:grid-cols-2 gap-8 mt-10">
            {post.gallery.map((g) => (
              <figure key={g.url}>
                <img src={g.url} alt={g.caption} className="w-full h-80 object-cover" />
                <figcaption className="text-sm text-ink/60 mt-3 italic">{g.caption}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <img src={post.image} alt={post.title} className="w-full h-[420px] object-cover mt-10" />
        )}
        <div className="mt-10 space-y-6">
          {post.body.map((para, i) => (
            <p key={i} className="text-lg leading-relaxed text-ink/80">{para}</p>
          ))}
        </div>
        <div className="mt-14 border-t border-black/10 pt-10">
          <Link to="/consult" data-testid="blog-consult-cta" className="inline-flex items-center gap-2 bg-ink text-white overline px-9 py-4 hover:bg-gold transition-colors">Book a 1:1 Color Consult <ArrowRight size={15} /></Link>
        </div>
      </article>
      <QuoteCTA />
    </div>
  );
}
